import { ArrowLeftOutlined } from "@ant-design/icons";
import { Menu as AntMenu, Dropdown } from "antd";
import "@sendbird/uikit-react/dist/index.css";
import { useHistory } from "react-router";
import "../chatStyle.scss";
import { logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "src/constants/firebaseEvents";
import { logProfileNavigated } from "src/containers/workers/logs";
import { WhoCalledRatingPerformanceModal } from "@src/appV2/components/BlockWorkersAndGetDonuts/constants";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { getChannelTitle } from "../channel";
import { Text, UseModalState, useModalState } from "@src/lib/ui-react";
import { useWorker } from "@src/appV2/Worker/api/useWorker";
import { useShiftInfo } from "../utils/useShiftInfo";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, Tooltip, Menu, MenuItem, ButtonGroup } from "@mui/material";
import { ArrowDropDown, Send, AccountBox, WarningAmber, PersonOff } from "@mui/icons-material";
import { FacilityStatusObj } from "@src/appV2/redesign/utils/facility";
import {
  NO_MATCHING_QUALIFICATION_TOOLTIP_MESSAGE,
  PENDING_ACCOUNT_TOOLTIP_MESSAGE,
} from "@src/appV2/Calendar/constants";
import { ChatChannel } from "../chat.types";
import { ShiftInviteDialogWrapper } from "@src/containers/Shift/ShiftInvites/ShiftInviteDialogWrapper";
import { WorkerStatusLabel } from "@src/containers/Shift/ShiftInvites/Autocomplete/useGetNearbyWorkers";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { FACILITY_ROLES, FACILITY_USER_PERMISSIONS } from "@src/constants/facility";
import { useState, useEffect, useRef } from "react";
import { useGetQualifications } from "@src/appV2/Admin/Qualifications/api/useGetQualifications";
import { StarredProfessionalConfirmationModal } from "@src/appV2/Professionals/StarredProfessionals/components/starredProfessionalConfirmationModal";
import { useWorkplaceStarredProfessionals } from "@src/appV2/Professionals/api/starredProfessionals/useWorkplaceStarredProfessionals";
import { usePostStarredProfessional } from "@src/appV2/Professionals/api/starredProfessionals/usePostStarredProfessional";
import { useDeleteStarredProfessional } from "@src/appV2/Professionals/api/starredProfessionals/useDeleteStarredProfessional";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { ShareCredentialsModal } from "@src/appV2/WorkerCredentials/ShareCredentialsModal";
import { ShareCredentialsChatButton } from "@src/containers/chat/components/ShareCredentialsChatButton";
import { useCredentialSharing } from "@src/appV2/Professionals/MyProfessionals/hooks/useCredentialSharing";
import { areCredentialsMissing } from "@src/appV2/WorkerCredentials/areCredentialsMissing";
import { useGetWorkerCredentials } from "@src/appV2/WorkerCredentials/api/useGetWorkerCredentials";
import { getWorkplaceConfiguredQualifications } from "@src/appV2/Professionals/utils/getWorkerWorkplaceConfiguredQualifications";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { useShowInHomeLockToggle } from "@src/appV2/ExperimentalHomeHealth/lib/useShowInHomeLockToggle";
import { useShiftManagementPermission } from "@src/appV2/redesign/DailyView/hooks/useShiftManagementPermission";
import { InHomeLockToggle } from "@src/appV2/redesign/Chat/InHomeLock/components/InHomeLockToggle";
import { Avatar } from "@src/appV2/lib/Avatar";
import { StarProfessionalAction } from "@src/appV2/Professionals/MyProfessionals/ProfessionalsList/card/Actions";
import { BorderedIconButton } from "@src/appV2/components/BorderedIconButton";
import { Button } from "@src/appV2/redesign/components/Button";
import { useChartingCredentialProvisioningConfig } from "@src/appV2/WorkerCredentials/api/useCredentialProvisioningConfig";
import { WorkerRequestedCancelModal } from "@src/components/WorkerRequestedCancelModal";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";

export interface ChatHeaderProps {
  workerId: string;
  workplaceId: string;
  workplaceUserId: string;
  currentChannel?: ChatChannel;
  backAction: boolean;
  facilityStatus: (typeof FacilityStatusObj)[keyof typeof FacilityStatusObj];
  isBlocked: boolean;
  matchingQualifications: string[];
  hasWorkedAtWorkplace: boolean;
  defaultWorkerQualification?: string;
  manageCredentialsModalState?: UseModalState;
  createOrResetCredentialsModalState?: UseModalState;
}

export function ChatHeader(props: ChatHeaderProps) {
  const {
    workerId,
    workplaceId,
    workplaceUserId,
    currentChannel,
    backAction,
    facilityStatus,
    isBlocked,
    manageCredentialsModalState,
    matchingQualifications,
    hasWorkedAtWorkplace,
    defaultWorkerQualification,
  } = props;
  const editShiftInviteModalState = useModalState();
  const shareCredentialsModalState = useModalState();
  const workerCancelModalModalState = useModalState();
  const { data: worker, isSuccess: isWorkerSuccess } = useWorker(workerId);
  const { showErrorToast } = useToast();
  const {
    isEnabled: isCredentialSharingEnabled,
    isChartingAndTimeClock,
    isChartingOnly,
    isTimeClockOnly,
  } = useCredentialSharing();
  const { data: workerCredentialsResponse, isLoading: isLoadingChartingCredentials } =
    useGetWorkerCredentials({
      filter: { workerIds: workerId, workplaceId },
    });
  const { enabled: isChartingCredentialProvisioningEnabled } =
    useChartingCredentialProvisioningConfig();
  const { data: workplaceProfile } = useGetFacilityProfile(workplaceId ?? "", "tmz rates", {
    enabled: isDefined(workplaceId),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const workplaceConfiguredQualifications = getWorkplaceConfiguredQualifications({
    rates: workplaceProfile?.rates,
  });
  const workerCredentials = workerCredentialsResponse?.data[0]?.attributes ?? {};
  const missingCredentials = areCredentialsMissing({
    isChartingAndTimeClock,
    isChartingOnly,
    isTimeClockOnly,
    credentials: { ...workerCredentials, workerId, workplaceId },
  });

  const shiftInfo = useShiftInfo(workplaceId, worker?.qualification);
  const history = useHistory();
  const { admin, user, type: userType } = useSession();
  const { access = [], permissions = [] } = user ?? {};
  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const canPostShiftInvite =
    !isHomeHealthEnabled &&
    (admin ||
      access.includes(FACILITY_ROLES.ADMIN) ||
      access.includes(FACILITY_ROLES.SHIFT_MANAGEMENT) ||
      permissions.includes(FACILITY_USER_PERMISSIONS.POST_SHIFT_PERMISSION));

  const { canCancelShifts } = useShiftManagementPermission();

  const isWorkerRequestedCancelAlertEnabled = useCbhFlag(
    CbhFeatureFlag.WORKER_REQUESTED_CANCEL_ALERT,
    { defaultValue: false }
  );

  const showLockToggle = useShowInHomeLockToggle();

  const handleWorkerNameClicked = () => {
    const location = getLocation("workplaceWorkerDetails", { pathParams: { workerId } });
    window.open(`${window.location.origin}${location.pathname}`, "_blank");
    logProfileNavigated({
      workerId,
      workplaceId,
      workplaceUserId,
      location: WhoCalledRatingPerformanceModal.CHAT_CHANNEL,
    });
  };

  // Get qualification details for display labels
  const { data: qualificationsData } = useGetQualifications();
  const allQualifications = qualificationsData?.data || [];
  const qualificationDisplayLabels = new Map(
    allQualifications.map((q) => [q.attributes.name, q.attributes.displayLabel])
  );

  // State for dropdown menu and dialog qualification
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dialogQualification, setDialogQualification] = useState<string | undefined>(undefined);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Reset states when switching chats or default qualification changes
  useEffect(() => {
    setDropdownOpen(false);
    setDialogQualification(undefined);
  }, [workerId, workplaceId, currentChannel?.metadata?.agentReq, defaultWorkerQualification]);

  // Available qualifications for dropdown (excluding the default one)
  const availableQualifications = matchingQualifications.filter(
    (qual) =>
      qual !== defaultWorkerQualification && workplaceConfiguredQualifications.includes(qual)
  );
  const hasOtherQualificationsAvailable = availableQualifications.length >= 1;
  const shouldShowSplitButton = hasOtherQualificationsAvailable && defaultWorkerQualification;
  const handleInviteClick = () => {
    setDialogQualification(defaultWorkerQualification);
    editShiftInviteModalState.openModal();
    logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
      action: "CHAT_MODAL_OPEN",
      workplaceId,
      workplaceUserId,
      workerType: defaultWorkerQualification,
      ...shiftInfo,
    });
  };

  const handleQualificationSelect = (qualification: string) => {
    setDropdownOpen(false);
    setDialogQualification(qualification);

    // Immediately send invite with the selected qualification
    editShiftInviteModalState.openModal();
    logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
      action: "CHAT_MODAL_OPEN",
      workplaceId,
      workplaceUserId,
      workerType: qualification,
      ...shiftInfo,
    });
  };

  const handleDropdownToggle = () => {
    setDropdownOpen((prevOpen) => !prevOpen);
  };

  const handleDropdownClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setDropdownOpen(false);
  };

  const starredProfessionalConfirmationModalState = useModalState();

  const { isStarredProfessionalEnabled, workplaceHasStarredProfessionals, starredProfessionalId } =
    useWorkplaceStarredProfessionals({ workplaceId, workerId });
  const [isStarred, setIsStarred] = useState(
    isDefined(starredProfessionalId) && isStarredProfessionalEnabled
  );
  const { mutateAsync: starProfessional } = usePostStarredProfessional({
    workplaceId,
    professionalId: workerId,
  });
  const { mutateAsync: deleteStarredProfessional } = useDeleteStarredProfessional({
    workplaceId,
    starredProfessionalId,
  });

  const handleStarProfessional = async () => {
    if (isBlocked) {
      logEvent(APP_V2_USER_EVENTS.STARRED_PROFESSIONAL_ATTEMPTED_BUT_BLOCKED, {
        workerId,
        workplaceId,
        workplaceUserId: user?._id,
      });
      showErrorToast("Professionals on your DNR list cannot be added to your starred list");
      return;
    }
    if (workplaceHasStarredProfessionals) {
      setIsStarred((previous) => !previous);
      const action = isStarred
        ? async () => {
            if (!starredProfessionalId) {
              throw new Error("Missing starred professional id");
            }

            await deleteStarredProfessional(undefined);
          }
        : starProfessional;
      const event = isStarred
        ? APP_V2_USER_EVENTS.REMOVED_STARRED_PROFESSIONAL
        : APP_V2_USER_EVENTS.STARRED_PROFESSIONAL;

      logEvent(event, {
        isStarred,
        workerId,
        workplaceId,
        workplaceUserId: user?._id,
        source: "Chat",
      });
      try {
        await action();
      } catch {
        setIsStarred((previous) => !previous);
      }
    } else {
      logEvent(APP_V2_USER_EVENTS.STARRED_PROFESSIONAL_CONFIRMATION_MODAL_OPENED, {
        workerId,
        workplaceId,
        workplaceUserId: user?._id,
      });
      starredProfessionalConfirmationModalState.openModal();
    }
  };

  const getQualificationDisplayName = (qualification: string) => {
    return qualificationDisplayLabels.get(qualification) || qualification;
  };

  const isFacilityAccountPending = facilityStatus === FacilityStatusObj.PENDING;
  const hasDefaultQualification = isDefined(defaultWorkerQualification);
  const isShiftInviteDisabled = isFacilityAccountPending || !hasDefaultQualification;

  const inviteDisabledTooltip = isFacilityAccountPending
    ? PENDING_ACCOUNT_TOOLTIP_MESSAGE
    : NO_MATCHING_QUALIFICATION_TOOLTIP_MESSAGE;

  const toolTip = canPostShiftInvite ? "" : inviteDisabledTooltip;

  return (
    <div className="custom-header">
      <div className="custom-header-title">
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          width="100%"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap={2}>
            {backAction && (
              <ArrowLeftOutlined
                className="close-channel"
                onClick={() => history.push(getLocation("facilityChat"))}
              />
            )}

            <Avatar name={worker?.name || "User"} src={worker?.profileImageUrl ?? ""} />

            <Stack direction="column">
              <Text variant="h3" color="text.primary" bold>
                {getChannelTitle(currentChannel, userType)}
              </Text>

              {isDefined(defaultWorkerQualification) && (
                <Text variant="h4" color="text.secondary">
                  {getQualificationDisplayName(defaultWorkerQualification)}
                </Text>
              )}
            </Stack>
          </Stack>

          {/* Only show action buttons when viewing from facility perspective */}
          {userType !== "AGENT" && (
            <Stack direction="row" spacing={1}>
              {showLockToggle && (
                <BerlinThemeProvider>
                  <InHomeLockToggle workerId={workerId} workplaceId={workplaceId} />
                </BerlinThemeProvider>
              )}
              {canPostShiftInvite && (
                <Tooltip title={toolTip} arrow>
                  <span>
                    {shouldShowSplitButton ? (
                      <ButtonGroup ref={anchorRef} disableElevation sx={{ height: "100%" }}>
                        <Button
                          onClick={handleInviteClick}
                          disabled={isShiftInviteDisabled}
                          sx={(theme) => ({
                            textTransform: "none",
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              border: `1px solid ${theme.palette.divider}`,
                            },
                          })}
                          variant="outlined"
                        >
                          {/* Include Send icon as child instead of passing to Button's startIcon
                            prop to allow for styling overrides. Apply same margins as startIcon */}
                          <Send sx={{ fontSize: 20, mr: 1, ml: -0.5 }} />
                          Invite as {getQualificationDisplayName(defaultWorkerQualification)}
                        </Button>
                        <Button
                          size="small"
                          onClick={handleDropdownToggle}
                          disabled={isShiftInviteDisabled}
                          variant="outlined"
                          sx={(theme) => ({
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.default,
                            px: 1,
                            "&:hover": {
                              border: `1px solid ${theme.palette.divider}`,
                            },
                          })}
                        >
                          <ArrowDropDown />
                        </Button>
                      </ButtonGroup>
                    ) : (
                      <Button
                        onClick={handleInviteClick}
                        variant="outlined"
                        disabled={isShiftInviteDisabled}
                        sx={(theme) => ({
                          height: "100%",
                          textTransform: "none",
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.default,
                          color: theme.palette.primary.main,
                        })}
                      >
                        <Send sx={{ fontSize: 20, mr: 1, ml: -0.5 }} />
                        Invite Professional
                      </Button>
                    )}
                  </span>
                </Tooltip>
              )}

              {isWorkerRequestedCancelAlertEnabled && canCancelShifts && (
                <Dropdown
                  overlay={
                    <AntMenu>
                      <AntMenu.Item
                        key="worker-requested-cancel"
                        onClick={() => workerCancelModalModalState.openModal()}
                        icon={<PersonOff fontSize="small" sx={{ mr: 1 }} />}
                      >
                        Worker asked me to cancel
                      </AntMenu.Item>
                    </AntMenu>
                  }
                  trigger={["click"]}
                >
                  <BorderedIconButton tooltip="Report">
                    <WarningAmber sx={{ fontSize: 20, color: "#faad14" }} />
                  </BorderedIconButton>
                </Dropdown>
              )}

              <StarProfessionalAction
                isStarred={isStarred}
                visible={isStarredProfessionalEnabled && hasWorkedAtWorkplace}
                handleStarProfessional={handleStarProfessional}
                disabled={false}
              />

              {isChartingCredentialProvisioningEnabled ? (
                <ShareCredentialsChatButton
                  tooltip="View PCC status"
                  visible={isCredentialSharingEnabled}
                  isLoading={isLoadingChartingCredentials}
                  handleClick={() => manageCredentialsModalState?.openModal()}
                />
              ) : (
                <ShareCredentialsChatButton
                  tooltip={missingCredentials ? "Share credentials" : "Edit credentials"}
                  visible={isCredentialSharingEnabled}
                  isLoading={isLoadingChartingCredentials}
                  handleClick={() => shareCredentialsModalState.openModal()}
                />
              )}

              <BorderedIconButton
                tooltip="View profile"
                onClick={() => {
                  handleWorkerNameClicked();
                }}
              >
                <AccountBox />
              </BorderedIconButton>
            </Stack>
          )}

          {/* Dropdown Menu */}
          <Menu
            open={dropdownOpen}
            anchorEl={anchorRef.current}
            onClose={handleDropdownClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {availableQualifications.map((qualification) => (
              <MenuItem
                key={qualification}
                onClick={() => handleQualificationSelect(qualification)}
              >
                Invite as {getQualificationDisplayName(qualification)}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </div>
      {isWorkerSuccess &&
        isDefined(shiftInfo) &&
        isDefined(dialogQualification) &&
        editShiftInviteModalState.modalIsOpen && (
          <ShiftInviteDialogWrapper
            workerType={dialogQualification}
            modalState={editShiftInviteModalState}
            shiftInfo={shiftInfo}
            initialActiveState={0}
            initialSelectedWorker={{
              workerId: worker.userId,
              phoneNumber: worker.phone,
              fullName: worker.name,
              statusLabel: isBlocked ? WorkerStatusLabel.Blocked : undefined,
            }}
          />
        )}
      {isDefined(worker) && (
        <StarredProfessionalConfirmationModal
          isOpen={starredProfessionalConfirmationModalState.modalIsOpen}
          onClose={starredProfessionalConfirmationModalState.closeModal}
          workerName={worker.name}
          workplaceId={workplaceId}
          workerId={workerId}
          key={workerId}
          setIsStarred={setIsStarred}
        />
      )}
      {shareCredentialsModalState.modalIsOpen && (
        <ShareCredentialsModal
          workerId={workerId}
          workplaceId={workplaceId}
          source="workplace-chat"
          workerName={worker?.name}
          onClose={shareCredentialsModalState.closeModal}
          onSuccess={() => {}}
        />
      )}
      <WorkerRequestedCancelModal
        modalState={workerCancelModalModalState}
        workerId={workerId}
        workerName={worker?.name}
        facilityId={workplaceId}
        workerQualification={
          defaultWorkerQualification
            ? getQualificationDisplayName(defaultWorkerQualification)
            : undefined
        }
      />
    </div>
  );
}
